/**
 * WONE-612: unified email-first entry screen.
 *
 * Single email field → `learnerAuthMethods(email)` → state machine:
 *   - UNKNOWN_COMPANY        → /auth/dead-end
 *   - methods.length === 0   → inline "sign-in unavailable"
 *   - methods.length === 1   → auto-pick via navigateToMethod
 *   - methods.length >= 2    → /method-chooser (chooser) with state
 *
 * Mobile parity: src/app/(auth)/email-entry/index.tsx (PR #820).
 */

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useLazyQuery } from "@apollo/client";
import { useTranslation } from "lib/i18n/use-translation";

import appInfo from "utility/app-info";
import { validateEmail } from "utility/Function";
import { LEARNER_AUTH_METHODS } from "utility/graphQl/query";
import { getLastUsedEmail } from "utility/last-used-email";
import {
  EMAIL_DRAFT_KEY,
  JUST_LOGGED_OUT_KEY,
} from "providers/auth-storage-keys";
import { trackEvent } from "utility/mixpanel";

import {
  AuthLayout,
  Heading,
  LegalFooter,
  PrimaryButton,
  Subtitle,
  TextField,
} from "apps/user/common/components/auth-layout";

import {
  AuthUserState,
  HIDDEN_METHODS,
  navigateToMethod,
} from "./navigate-to-method";

import styles from "./email-entry.module.scss";

type AuthMethod = { id: string; name: string };

type LearnerAuthMethodsResult = {
  learnerAuthMethods: {
    userState: AuthUserState;
    methods: AuthMethod[];
  } | null;
};

const EmailEntry: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(["auth", "common"]);

  // ``?email=`` query param prefills (Branch.io invite link drop, see
  // WONE-613). Otherwise fall back to the last-used email (mobile
  // parity, PR #828) → then the in-flight sessionStorage draft for
  // back-nav cases.
  const queryEmail = searchParams.get("email") || "";
  const [email, setEmail] = useState<string>(
    queryEmail ||
      getLastUsedEmail() ||
      sessionStorage.getItem(EMAIL_DRAFT_KEY) ||
      ""
  );

  // One-shot flag set by a forced sign-out in ``providers/auth.js``.
  // Consume it on mount so the back chevron disappears for the first paint
  // and re-renders show the chevron again if the user navigates into here
  // from elsewhere. Mobile parity: a forced sign-out shouldn't surface a
  // dead back arrow.
  const [hideBack] = useState<boolean>(() => {
    try {
      const flag = sessionStorage.getItem(JUST_LOGGED_OUT_KEY) === "1";
      if (flag) sessionStorage.removeItem(JUST_LOGGED_OUT_KEY);
      return flag;
    } catch {
      return false;
    }
  });

  const [inlineError, setInlineError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // The path the user was trying to reach before sign-in (carried
  // through SSO via PKCE state). React-router stores it on
  // ``location.state.from``; we serialise it back to a single string.
  const returnTo: string | undefined = (() => {
    const from = (location.state as { from?: { pathname: string; search?: string } } | null)
      ?.from;
    if (!from) return undefined;
    return `${from.pathname}${from.search ?? ""}`;
  })();

  // Surface upstream ``?error=`` from a failed SSO round-trip or a
  // forced sign-out, then strip it from the URL so a re-render or back
  // navigation doesn't re-stamp the error over a fresh user action.
  useEffect(() => {
    const upstreamError = searchParams.get("error");
    if (!upstreamError) return;
    setInlineError(upstreamError);
    const next = new URLSearchParams(searchParams);
    next.delete("error");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const [fetchAuthMethods, { loading: methodsLoading }] = useLazyQuery<
    LearnerAuthMethodsResult
  >(LEARNER_AUTH_METHODS, { fetchPolicy: "network-only" });

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    event.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setInlineError("Please enter your work email");
      trackEvent("Auth Validation Error Shown", {
        Field: "email",
        Screen: "email_entry",
        "Error Code": "empty",
      });
      return;
    }
    // Velislav review (#1172 nit): basic email-format guard so a typo
    // doesn't get rendered back as "We couldn't find your company".
    // ``validateEmail`` (utility/Function.js) is the same regex the rest
    // of the app uses for client-side email checks.
    if (!validateEmail(trimmed)) {
      setInlineError("That doesn't look like a valid email address.");
      trackEvent("Auth Validation Error Shown", {
        Field: "email",
        Screen: "email_entry",
        "Error Code": "invalid_format",
      });
      return;
    }
    setInlineError(null);
    sessionStorage.setItem(EMAIL_DRAFT_KEY, trimmed);
    setSubmitting(true);
    trackEvent("Auth Email Submitted", { "Has Value": true });

    try {
      const { data } = await fetchAuthMethods({
        variables: { appInfo, email: trimmed },
      });
      const result = data?.learnerAuthMethods;
      if (!result) {
        setInlineError("Something went wrong. Please try again.");
        trackEvent("Auth Methods Error", { "Error Type": "empty_response" });
        trackEvent("Auth Server Error Shown", {
          Screen: "email_entry",
          "Error Code": "empty_response",
        });
        return;
      }

      if (result.userState === "UNKNOWN_COMPANY") {
        // Mobile parity (PR #820): no separate dead-end route — render
        // the explanation inline under the email field so the user can
        // correct their typo without losing context.
        setInlineError(
          "We couldn't find your company. Please double-check your " +
            "email or contact your HR team — if the problem persists, " +
            "email support@walkingonearth.com."
        );
        trackEvent("Auth Methods Resolved", { State: "unknown_company" });
        // Mobile parity (WONE-714): the inline error the user actually
        // sees is tracked separately from the funnel-state event above.
        trackEvent("Auth Server Error Shown", {
          Screen: "email_entry",
          "Error Code": "unknown_company",
        });
        return;
      }

      const methods = result.methods.filter((m) => !HIDDEN_METHODS.has(m.id));

      if (methods.length === 0) {
        setInlineError(
          "Sign-in for this email isn't currently available. Please contact your administrator."
        );
        trackEvent("Auth Methods Resolved", { State: "no_methods" });
        trackEvent("Auth Server Error Shown", {
          Screen: "email_entry",
          "Error Code": "no_methods",
        });
        return;
      }

      trackEvent("Auth Methods Resolved", {
        State: "resolved",
        Methods: methods.map((m) => m.id),
        "User State": result.userState,
      });

      if (methods.length === 1) {
        const dispatched = await navigateToMethod({
          methodId: methods[0].id,
          email: trimmed,
          userState: result.userState,
          navigate,
          returnTo,
        });
        // Velislav review (#1172 nit): if the resolver auto-picked an
        // unknown id, the dispatcher would no-op + warn into the
        // console — invisible to the user. Surface it.
        if (!dispatched) {
          setInlineError(
            "Sign-in method isn't supported on web yet. Please contact support@walkingonearth.com."
          );
          trackEvent("Auth Server Error Shown", {
            Screen: "email_entry",
            "Error Code": "method_not_supported",
          });
        }
        return;
      }

      navigate("/method-chooser", {
        state: {
          email: trimmed,
          userState: result.userState,
          methods,
          returnTo,
        },
      });
    } catch {
      setInlineError("Something went wrong. Please try again.");
      trackEvent("Auth Methods Error", { "Error Type": "network" });
      trackEvent("Auth Server Error Shown", {
        Screen: "email_entry",
        "Error Code": "network",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const loading = methodsLoading || submitting;

  return (
    <AuthLayout
      variant="form"
      // Email-entry is the new top of the auth funnel — back chevron
      // returns to the branded intro, except after a forced sign-out.
      hideBack={hideBack}
      onBack={() => navigate("/get-started")}
    >
      <form
        onSubmit={handleSubmit}
        className={styles["form"]}
        autoComplete="email"
      >
        <Heading>{t("auth:emailEntry.title")}</Heading>
        <Subtitle>{t("auth:emailEntry.subtitle")}</Subtitle>

        <TextField
          label={t("auth:emailEntry.fieldLabel")}
          type="email"
          name="email"
          autoComplete="email"
          placeholder={t("auth:emailEntry.placeholder")}
          required
          data-testid="auth-email-entry-input"
          trackingScreen="email_entry"
          trackingField="email"
          value={email}
          onChange={(event) => {
            const next = event.target.value;
            setEmail(next);
            if (next) sessionStorage.setItem(EMAIL_DRAFT_KEY, next);
            else sessionStorage.removeItem(EMAIL_DRAFT_KEY);
            if (inlineError) setInlineError(null);
          }}
          // Render the unknown-company / no-methods / transport error
          // inside the field's own helper slot so the spacing matches
          // mobile (tight under the input, not the 16px form-row gap).
          error={inlineError}
        />

        <div className={styles["submit"]}>
          <PrimaryButton
            type="submit"
            loading={loading}
            data-testid="auth-email-entry-continue"
          >
            {t("common:action.continue")}
          </PrimaryButton>
        </div>

        <LegalFooter />
      </form>
    </AuthLayout>
  );
};

export default EmailEntry;
